import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTimeOff } from '@/stores/timeOff';
import { useStaff } from '@/stores/staff';
import { useRole, useUser } from '@/stores/identity';
import {
  TIME_OFF_STATUS_LABELS,
  TIME_OFF_TYPES,
  formatTimeOffRange,
  listConflictingShifts,
  timeOffDayCount,
} from '@/lib/timeOff';
import { formatTime } from '@/lib/scheduling';
import { humanize } from '@/lib/errors';
import { notify } from '@/lib/notify';
import {
  CrossOrgReadOnlyBanner,
  useIsReadOnlyCrossOrg,
} from '@/components/CrossOrgReadOnlyBanner';
import type { TimeOffConflictShift, TimeOffStatus } from '@/lib/types';

/**
 * Time-off request detail.
 *
 * Shows the request, the current status, the conflict panel for pending
 * approval (with shifts that would collide if approved), and the action
 * buttons appropriate to the caller's permissions and the current status.
 */
export default function TimeOffDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const role = useRole();
  const user = useUser();

  const status = useTimeOff((s) => s.status);
  const items = useTimeOff((s) => s.items);
  const load = useTimeOff((s) => s.load);
  const decide = useTimeOff((s) => s.decide);
  const cancel = useTimeOff((s) => s.cancel);
  const withdraw = useTimeOff((s) => s.withdraw);

  const staffStatus = useStaff((s) => s.status);
  const staffItems = useStaff((s) => s.items);
  const loadStaff = useStaff((s) => s.load);

  const [conflicts, setConflicts] = useState<TimeOffConflictShift[]>([]);
  const [conflictsStatus, setConflictsStatus] = useState<
    'idle' | 'loading' | 'loaded' | 'error'
  >('idle');
  const [actionError, setActionError] = useState<string | null>(null);
  const [decisionNote, setDecisionNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (status === 'idle') void load();
    if (staffStatus === 'idle') void loadStaff();
  }, [status, staffStatus, load, loadStaff]);

  const req = useMemo(() => items.find((it) => it.id === id) ?? null, [items, id]);
  const staffRow = useMemo(
    () => (req ? staffItems.find((s) => s.id === req.staff_id) : null),
    [req, staffItems]
  );

  // Load conflicts whenever we have a pending or approved request.
  const reqId = req?.id;
  const reqStatus = req?.status;
  useEffect(() => {
    if (!reqId) return;
    if (reqStatus !== 'pending' && reqStatus !== 'approved') return;
    setConflictsStatus('loading');
    listConflictingShifts(reqId)
      .then((rows) => {
        setConflicts(rows);
        setConflictsStatus('loaded');
      })
      .catch(() => setConflictsStatus('error'));
  }, [reqId, reqStatus]);

  // Cross-org admin: gate all write surfaces. Server-side the time-off
  // RPCs (decide / cancel / withdraw) refuse to act on rows belonging to
  // a different organization, so showing the buttons would just produce
  // a generic 42501. Hide them and surface a banner instead. Hook is
  // called above any early returns so the hook order is stable across
  // renders.
  const isReadOnlyCrossOrg = useIsReadOnlyCrossOrg(staffRow?.organization_id);

  if (!req) {
    return (
      <div className="container mx-auto py-8 px-4">
        <p className="text-sm text-muted-foreground">
          {status === 'loading' ? 'Loading…' : 'Request not found.'}
        </p>
      </div>
    );
  }

  const typeLabel =
    TIME_OFF_TYPES.find((t) => t.value === req.type)?.label ?? req.type;
  const days = timeOffDayCount(req);
  const isAdmin = role === 'owner' || role === 'manager';
  const isOwnRequest = !!staffRow && staffRow.profile_id === user?.id;

  const canDecide = isAdmin && req.status === 'pending' && !isReadOnlyCrossOrg;
  const canWithdraw =
    isOwnRequest && req.status === 'pending' && !isReadOnlyCrossOrg;
  const canCancel =
    isAdmin &&
    (req.status === 'pending' || req.status === 'approved') &&
    !isReadOnlyCrossOrg;

  async function handleDecide(target: Extract<TimeOffStatus, 'approved' | 'denied'>) {
    if (!req) return;
    setActionError(null);
    setBusy(true);
    try {
      await decide(req.id, target, decisionNote.trim() || null);
      setDecisionNote('');
      notify.success(target === 'approved' ? 'Time-off approved' : 'Time-off denied');
    } catch (err) {
      setActionError(humanize(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleWithdraw() {
    if (!req) return;
    setActionError(null);
    setBusy(true);
    try {
      await withdraw(req.id, null);
      notify.success('Request withdrawn');
    } catch (err) {
      setActionError(humanize(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleCancel() {
    if (!req) return;
    const reason = window.prompt(
      'Cancellation reason (visible in the audit log):'
    );
    if (reason === null) return;
    setActionError(null);
    setBusy(true);
    try {
      await cancel(req.id, reason.trim() || null);
      notify.success('Time-off cancelled');
    } catch (err) {
      setActionError(humanize(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {isReadOnlyCrossOrg && (
          <CrossOrgReadOnlyBanner
            entityLabel="time-off request"
            blockedActionsLabel="approvals, denials, withdrawals, and cancellations"
          />
        )}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="space-y-1">
            <button
              onClick={() => navigate('/time-off')}
              className="text-xs text-muted-foreground hover:underline"
            >
              ← Back to time off
            </button>
            <h1 className="text-2xl font-semibold tracking-tight">
              {staffRow?.display_name ?? 'Time-off request'}
            </h1>
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <StatusPill status={req.status} />
              <span>{typeLabel}</span>
              <span>·</span>
              <span>
                {days} {days === 1 ? 'day' : 'days'}
              </span>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-6 space-y-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Dates
            </p>
            <p className="text-sm">{formatTimeOffRange(req)}</p>
          </div>
          {req.reason && (
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Reason
              </p>
              <p className="text-sm whitespace-pre-wrap">{req.reason}</p>
            </div>
          )}
          {req.decision_note && (
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Decision note
              </p>
              <p className="text-sm whitespace-pre-wrap">{req.decision_note}</p>
            </div>
          )}
          {req.cancellation_reason && (
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Cancellation reason
              </p>
              <p className="text-sm whitespace-pre-wrap">
                {req.cancellation_reason}
              </p>
            </div>
          )}
        </div>

        {/* Conflict panel */}
        {(req.status === 'pending' || req.status === 'approved') && (
          <ConflictPanel
            requestStatus={req.status}
            conflicts={conflicts}
            conflictsStatus={conflictsStatus}
          />
        )}

        {/* Actions */}
        {(canDecide || canWithdraw || canCancel) && (
          <div className="rounded-lg border border-border bg-card p-6 space-y-3">
            {canDecide && (
              <>
                <label
                  className="text-xs uppercase tracking-wide text-muted-foreground"
                  htmlFor="note"
                >
                  Decision note (optional)
                </label>
                <textarea
                  id="note"
                  rows={2}
                  value={decisionNote}
                  onChange={(e) => setDecisionNote(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder="Why approved/denied (kept in audit log)"
                />
              </>
            )}
            {actionError && (
              <p className="rounded-md border border-destructive bg-destructive/10 p-2 text-sm text-destructive">
                {actionError}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {canDecide && (
                <>
                  <button
                    onClick={() => handleDecide('approved')}
                    disabled={busy}
                    className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleDecide('denied')}
                    disabled={busy}
                    className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                  >
                    Deny
                  </button>
                </>
              )}
              {canWithdraw && (
                <button
                  onClick={handleWithdraw}
                  disabled={busy}
                  className="rounded-md border border-border bg-background px-4 py-2 text-sm hover:bg-muted/40 disabled:opacity-50"
                >
                  Withdraw request
                </button>
              )}
              {canCancel && req.status === 'approved' && (
                <button
                  onClick={handleCancel}
                  disabled={busy}
                  className="rounded-md border border-border bg-background px-4 py-2 text-sm hover:bg-muted/40 disabled:opacity-50"
                >
                  Cancel approval
                </button>
              )}
            </div>
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          <Link to="/schedule" className="hover:underline">
            Open schedules →
          </Link>
        </p>
      </div>
    </div>
  );
}

function ConflictPanel({
  requestStatus,
  conflicts,
  conflictsStatus,
}: {
  requestStatus: TimeOffStatus;
  conflicts: TimeOffConflictShift[];
  conflictsStatus: 'idle' | 'loading' | 'loaded' | 'error';
}) {
  if (conflictsStatus === 'loading') {
    return (
      <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
        Checking schedule conflicts…
      </div>
    );
  }
  if (conflictsStatus === 'error') {
    return (
      <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
        Couldn't load schedule conflicts.
      </div>
    );
  }
  if (conflicts.length === 0) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-900">
        No scheduled shifts conflict with this request.
      </div>
    );
  }

  const verb = requestStatus === 'approved' ? 'overlap' : 'would conflict if approved';

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-2">
      <p className="text-sm font-medium text-amber-900">
        {conflicts.length} shift{conflicts.length === 1 ? '' : 's'} {verb}.
      </p>
      <ul className="space-y-1 text-sm text-amber-900">
        {conflicts.map((c) => (
          <li key={c.shift_id} className="flex flex-wrap items-center gap-2">
            <span className="font-mono">{c.shift_date}</span>
            <span className="text-xs">
              {formatTime(c.start_time)}–{formatTime(c.end_time)}
            </span>
            <span className="text-xs text-amber-800">at {c.location_name}</span>
            {c.status !== 'draft' && (
              <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-medium uppercase">
                {c.status}
              </span>
            )}
          </li>
        ))}
      </ul>
      <p className="text-xs text-amber-800">
        Approving this request does <strong>not</strong> remove these shifts —
        cancel or reassign them in the schedule.
      </p>
    </div>
  );
}

function StatusPill({ status }: { status: TimeOffStatus }) {
  const styles: Record<TimeOffStatus, string> = {
    pending: 'bg-amber-100 text-amber-800',
    approved: 'bg-green-100 text-green-800',
    denied: 'bg-red-100 text-red-800',
    withdrawn: 'bg-gray-100 text-gray-700',
    cancelled: 'bg-gray-100 text-gray-700',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${styles[status]}`}
    >
      {TIME_OFF_STATUS_LABELS[status]}
    </span>
  );
}
